/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo } from "react";
import RoleToggle from "../../components/RoleToggle";
import { useFetchNotificationsData } from "../../apis/notificationApis";
import BannerNotificationModal from "../../components/common/BannerNotificationModal";

const Notification: React.FC = () => {
  const roles1 = ["All", "Read", "Unread"];
  const [activeRole1, setActiveRole1] = useState("All");
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [notification, setNotification] = useState<any>();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data, isPending: listPending, refetch } = useFetchNotificationsData(
    pageNo,
    searchFor
  );

  // ⏳ time ago formatter
  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // 📝 filter by read/unread
  const filteredNotifications = useMemo(() => {
    if (!data?.notifications) return [];

    switch (activeRole1) {
      case "Read":
        return data.notifications.filter((n: any) => n.seen); // assuming `sent = true` means read
      case "Unread":
        return data.notifications.filter((n: any) => !n.seen);
      default:
        return data.notifications;
    }
  }, [data, activeRole1]);

  // 📅 group notifications
  const groupedNotifications = useMemo(() => {
    const groups: Record<string, any[]> = {};
    const today = new Date().toDateString();

    filteredNotifications.forEach((n: any) => {
      const nDate = new Date(n.created_at).toDateString();
      const key = nDate === today ? "today" : nDate;
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });

    return groups;
  }, [filteredNotifications]);

  return (
    <div className="p-4 w-[1024px] mx-auto space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl text-[#212121]">All Notifications</h1>
        <RoleToggle
          options={roles1}
          active={activeRole1}
          onChange={setActiveRole1}
        />
      </div>

      {/* Today */}
      {groupedNotifications.today && (
        <div className="space-y-4">
          <p className="text-sm text-[#212121]/70 text-center">Today</p>
          {groupedNotifications.today.map((notification: any) => (
            <div
              key={notification.id}
              className="p-4 rounded-lg bg-[#F7F6F2] relative pr-6 space-y-2 cursor-pointer"
              onClick={() => {
                  setNotification(notification);
                  setIsOpen(true)
                }}
            >
              {notification.image ? (
                <div className="flex items-start gap-2">
                  <img
                    src={
                      notification.image.startsWith("http")
                        ? notification.image
                        : `${process.env.REACT_APP_IMAGE_BASE_URL}/${notification.image}`
                    }
                    alt={notification.title}
                    className="size-10 rounded-full object-cover"
                  />
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium">{notification.title}</h3>
                    <p className="text-[#212121]/90 text-sm">{notification.message}</p>
                    <p className="text-[#212121]/70 text-xs">
                      {timeAgo(notification.created_at)}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-sm font-medium">{notification.title}</h3>
                  <p className="text-[#212121]/90 text-sm">{notification.message}</p>
                  <p className="text-[#212121]/70 text-xs">
                    {timeAgo(notification.created_at)}
                  </p>
                </>
              )}

              {!notification.seen && (
                <div className="size-2 bg-red-500 rounded-full absolute right-4 top-4"></div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Other dates */}
      {Object.entries(groupedNotifications)
        .filter(([key]) => key !== "today")
        .map(([date, items]) => (
          <div key={date} className="space-y-4">
            <p className="text-sm text-[#212121]/70 text-center">{date}</p>
            {items.map((notification: any) => (
              <div
                key={notification.id}
                className="p-4 rounded-lg bg-[#F7F6F2] relative pr-6 space-y-2 cursor-pointer"
                onClick={() => {
                  setNotification(notification);
                  setIsOpen(true)
                }}
              >
                {notification.image ? (
                  <div className="flex items-start gap-2">
                    <img
                      src={
                        notification.image.startsWith("http")
                          ? notification.image
                          : `${process.env.REACT_APP_IMAGE_BASE_URL}/${notification.image}`
                      }
                      alt={notification.title}
                      className="size-10 rounded-full object-cover"
                    />
                    <div className="space-y-1">
                      <h3 className="text-sm font-medium">{notification.title}</h3>
                      <p className="text-[#212121]/90 text-sm">{notification.message}</p>
                      <p className="text-[#212121]/70 text-xs">
                        {timeAgo(notification.created_at)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-sm font-medium">{notification.title}</h3>
                    <p className="text-[#212121]/90 text-sm">{notification.message}</p>
                    <p className="text-[#212121]/70 text-xs">
                      {timeAgo(notification.created_at)}
                    </p>
                  </>
                )}

                {!notification.seen && (
                  <div className="size-2 bg-red-500 rounded-full absolute right-4 top-4"></div>
                )}
              </div>
            ))}
          </div>
        ))}

        <BannerNotificationModal 
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          notification={notification}
          refetch={refetch}
        />
    </div>
  );
};

export default Notification;
